<template>
    <div>
        <div class="pb--15">
            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else-if="pageLoadingErr">
                <EmptyPage state="error" type="task" @action="handleFetchTasks" />
            </template>

            <template v-else>
                <div class="w-100 pt--5 task__details__row">
                    <div class="task__details__row__left">
                        <div v-if="showActionBtns" class="task__details__row__header">
                            <FormLabel 
                                label-type="title" 
                                html-for="allTasks" 
                                title="All tasks" 
                            />
            
                            <div class="flex align-items-center gap-2">
                                <MainFilter page-type="tasks" />
                                <Group class="ml--5" :items="tasksGroup" :value="groupSelection" @select="setGroupSelection" />
                            </div>
                        </div>

                        <div class="task__details__row__list">
                            <template v-if="!tasks.length">
                                <EmptyPage v-if="isSearched" state="no_result" />
    
                                <EmptyPage
                                    v-else
                                    state="empty" type="task" icon-name="task"
                                />
                            </template>
    
                            <template v-else>
                                <TaskList 
                                    :group-selection="groupSelection"
                                    :tasks="tasks"
                                    :current-item-id="currentTaskId" 
                                    @set-current-task="setCurrentTask"
                                />
                            </template>
                            
                            <TaskForm  
                                :loading="addTaskLoading" 
                                @form-complete="handleAddTask"  
                            />
                        </div>
                    </div>

                    <div class="task__details__row__right">
                        <TaskDetails 
                            :current-item-id="currentTaskId" 
                            task-type="task" 
                            :group-selection="groupSelection"
                            @on-duplicate="handleDuplicateTask"
                            @on-delete="handleDeleteTask"
                            @set-current="setCurrentTask" 
                        />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import {
    EmptyPage,
    PageSpinner,
    MainFilter, 
    Group,
    FormLabel,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import { tasksGroup } from "@/utils/dummy";
import TaskDetails from "@/components/tasks/TaskDetails.vue";
import TaskList from "@/components/tasks/TaskList.vue";
import TaskForm from "@/components/tasks/TaskForm.vue";

export default {
    name: "TasksView",

    components: {
        EmptyPage,
        PageSpinner,
        MainFilter,
        TaskDetails, 
        Group,
        TaskList,
        TaskForm,
        FormLabel,
    },

    data() {
        return {
            pageLoading: false,
            pageLoadingErr: false,
            pageLoadingErrMsg: "",
            currentTaskId: "",
            taskIdToModify: "",
            tasksGroup,
            groupSelection: "none",
            addTaskLoading: false,
        };
    },

    computed: {
        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },

        tasks() {
            return this.$store.getters["task/tasks"] || [];
        },

        pageData() {
            return this.$store.getters["task/pageData"];
        },

        refreshList() {
            return this.$store.getters["task/refreshList"];
        },

        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {                
                await this.handleFetchTasks({ refresh: true, ...newVal });
            },
            deep: true,
        },

        "groupSelection": {
            async handler(newVal) {
                await this.handleFetchTasks({ refresh: true, groupBy: newVal });
            },
        },

        async refreshList(newVal) {
            if (newVal) {
                await this.handleFetchTasks({ refresh: true });
            }
        },
    },

    async mounted() {
        const groupSelection = localStorage.getItem("z_group_task_selection");

        if (groupSelection) {
            this.groupSelection = groupSelection;
            this.setCurrentTask(this.tasks[0]?.tasks[0]);
        } else {
            if (!this.tasks.length) {
                await this.handleFetchTasks({ refresh: true });
            }
        }
    },

    methods: {
        setGroupSelection(val) {
            this.groupSelection = val;
            localStorage.setItem("z_group_task_selection", val);
        },

        setCurrentTask(task) {                        
            if (!task?._id) return;
            this.currentTaskId = task._id;
        },

        resetCurrentTask() {                        
            this.currentTaskId = "";
        },

        async handleFetchTasks(query) {            
            try {
                if (!this.refreshList) {
                    this.pageLoading = true;
                    this.pageLoadingErr = false;
                    this.pageLoadingErrMsg = "";
                }

                const pageQueryObj = {
                    page: query?.page || 1,
                    groupBy: query?.groupBy || this.groupSelection,
                    ...query,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                
                await this.$store.dispatch("task/fetchTasks", { params: buildResult, groupBy: query?.groupBy });

                if (!this.refreshList) {
                    if (this.tasks.length) {
                        if (query?.groupBy !== "none" && this.tasks[0]?.tasks?.length) {
                            this.setCurrentTask(this.tasks[0]?.tasks[0]);
                        } else {
                            this.setCurrentTask(this.tasks[0]);
                        }
                    }
                }

                this.$store.dispatch("task/setRefreshList", false);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.pageLoadingErr = true;
                this.pageLoadingErrMsg = errMsg;
            } finally {
                if (!this.refreshList) {
                    this.pageLoading = false;
                }
            }
        }, 

        async handleAddTask(data) {
            try {
                this.addTaskLoading = true;

                const resp = await this.$store.dispatch("task/addTask", data.payload);
                this.$toast.success("Task added");                

                data.resetFn && data.resetFn();
                this.setCurrentTask(resp);

                if (this.tasks.length === 1) {
                    await this.$store.dispatch("app/getOnboardingChecks");
                }
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.addTaskLoading = false;
            }
        },

        async handleDuplicateTask(data) {
            return await this.$store.dispatch("task/duplicateTask", { task: data.task, payload: data.payload, type: data.type });
        },

        async handleDeleteTask(payload) {
            return await this.$store.dispatch("task/deleteTask", payload);
        },
    },
};
</script>